import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface CartItem {
  id: string;
  productId: number;
  variantId?: number;
  name: string;
  price: number;
  image: string;
  quantity: number;
  sku?: string;
}

export interface WishlistItem {
  id: number;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  slug: string;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "quantity"> & { quantity?: number }) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
}

interface WishlistStore {
  items: WishlistItem[];
  addItem: (item: WishlistItem) => void;
  removeItem: (id: number) => void;
  isInWishlist: (id: number) => boolean;
  clearWishlist: () => void;
  syncWithBackend: (token: string) => Promise<void>;
  addItemToBackend: (productId: number, token: string) => Promise<boolean>;
  removeItemFromBackend: (productId: number, token: string) => Promise<boolean>;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (item) => {
        const existingItem = get().items.find(
          (i) => i.id === item.id
        );
        if (existingItem) {
          set({
            items: get().items.map((i) =>
              i.id === item.id
                ? { ...i, quantity: i.quantity + (item.quantity || 1) }
                : i
            ),
          });
        } else {
          set({
            items: [...get().items, { ...item, quantity: item.quantity || 1 }],
          });
        }
      },
      removeItem: (id) => {
        set({ items: get().items.filter((i) => i.id !== id) });
      },
      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
        } else {
          set({
            items: get().items.map((i) =>
              i.id === id ? { ...i, quantity } : i
            ),
          });
        }
      },
      clearCart: () => set({ items: [] }),
      getTotal: () => {
        return get().items.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        );
      },
      getItemCount: () => {
        return get().items.reduce((count, item) => count + item.quantity, 0);
      },
    }),
    {
      name: "aarsa-cart",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useWishlistStore = create<WishlistStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (item) => {
        if (!get().isInWishlist(item.id)) {
          set({ items: [...get().items, item] });
        }
      },
      removeItem: (id) => {
        set({ items: get().items.filter((i) => i.id !== id) });
      },
      isInWishlist: (id) => {
        return get().items.some((i) => i.id === id);
      },
      clearWishlist: () => set({ items: [] }),
      syncWithBackend: async (token: string) => {
        try {
          const { wishlistApi } = await import("@/lib/api");
          const response = await wishlistApi.getAll(token);
          if (response.items) {
            const items: WishlistItem[] = response.items.map((item: any) => ({
              id: item.product_id,
              name: item.product_name,
              price: item.product_price,
              originalPrice: item.product_original_price,
              image: item.product_image_url || "/product1.png",
              slug: item.product_slug,
            }));
            set({ items });
          }
        } catch (error) {
          console.error("Failed to sync wishlist:", error);
        }
      },
      addItemToBackend: async (productId: number, token: string) => {
        try {
          if (!token) {
            console.error("No token provided for wishlist add");
            throw new Error("Authentication token is required");
          }
          const { wishlistApi } = await import("@/lib/api");
          console.log("Adding to wishlist with token:", token.substring(0, 20) + "...");
          const response = await wishlistApi.add(productId, token);
          
          // Backend wraps response in: { success, message, data: { is_in_wishlist, product_id } }
          const wrappedResponse = response;
          const data = wrappedResponse.data || wrappedResponse;
          const isInWishlist = data.is_in_wishlist || false;
          
          return isInWishlist;
        } catch (error: any) {
          console.error("Failed to add to wishlist:", error);
          console.error("Error details:", error.response?.data);
          throw error; // Re-throw to let the component handle it
        }
      },
      removeItemFromBackend: async (productId: number, token: string) => {
        try {
          const { wishlistApi } = await import("@/lib/api");
          const response = await wishlistApi.remove(productId, token);
          return !response.is_in_wishlist;
        } catch (error) {
          console.error("Failed to remove from wishlist:", error);
          return false;
        }
      },
    }),
    {
      name: "aarsa-wishlist",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

// Auth Store
export interface User {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: string;
}

interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (user: User) => void;
  updateToken: (token: string) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      login: (token, user) => {
        set({ token, user, isAuthenticated: true });
      },
      logout: () => {
        set({ token: null, user: null, isAuthenticated: false });
      },
      updateUser: (user) => {
        set({ user });
      },
      updateToken: (token) => {
        set({ token });
      },
    }),
    {
      name: "aarsa-auth",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

